/**
 * Composes a container's presentation label from already-resolved atomic fields.
 *
 * Pure: no store access, no i18n. This label is LOCALE-NEUTRAL - it embeds raw major/minor.
 * Surfaces that localise compose from atomic fields instead (the PDF does).
 *
 * Mirrors ContainerDisplayName::compose() in the PHP repo's src/Service/Swade/ContainerDisplayName.php.
 * Both are driven by the same fixture; see container-display-name.test.ts.
 */
export const SEPARATOR = ' \u2014 ';

/**
 * Every character either language's trim() or whitespace class can act on, named literally.
 *
 * NOT `\s`: JavaScript's `\s` is the fixed ECMAScript WhiteSpace + LineTerminator set, while PHP's
 * `\s` under the `/u` modifier is Unicode-aware. Measured over all 1,112,064 codepoints, PHP's `\s`
 * matches U+0085 and U+180E where JS's does not, so a `\s`-based rule lets the two languages emit
 * different labels for identical stored data. U+180E also moved from category Zs to Cf in Unicode
 * 6.3, so a table-driven definition could shift under either engine's Unicode-table upgrade. Naming
 * the set literally, by escape sequence rather than by shorthand, removes the dependency on both
 * engines' tables.
 *
 * U+0000 is included because PHP's default trim() removes it while JavaScript's does not.
 * U+FEFF is included because JavaScript's `\s` matches it while PCRE's does not.
 *
 * Mirrors WHITESPACE in the PHP repo's src/Service/Swade/ContainerDisplayName.php and must list the
 * identical set of codepoints:
 *
 *     /[\x{0000}\x{0009}-\x{000D}\x{0020}\x{0085}\x{00A0}\x{1680}\x{180E}\x{2000}-\x{200A}\x{2028}\x{2029}\x{202F}\x{205F}\x{3000}\x{FEFF}]/u
 */
const WHITESPACE = /[\u0000\u0009-\u000D\u0020\u0085\u00A0\u1680\u180E\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF]/g;

export const blankNormalise = (value: string | null | undefined): string | null => {
    if (value === null || value === undefined) return null;

    // Normalise BEFORE trimming. PHP's trim() and JavaScript's trim() remove different character
    // sets - JS strips NBSP, the U+2000 block and the BOM; PHP strips NUL instead - so trimming
    // alone would let the two languages emit different labels for identical stored data. Replacing
    // every whitespace character with a plain space first makes the subsequent trim() agree in
    // both, and also collapses an NBSP (or NUL) that sits INSIDE a value.
    //
    // Unlike the PHP mirror, there is no "malformed UTF-8" fallback here: a JavaScript string is
    // already-decoded UTF-16, so there is no byte-level validity failure for replace() to hit.
    const normalised = value.replace(WHITESPACE, ' ');

    const trimmed = normalised.trim();

    return trimmed === '' ? null : trimmed;
};

export function composeDisplayName(
    baseName: string | null,
    catalogName: string | null,
    severity: string | null,
    trapping: string | null,
): string {
    const base = blankNormalise(baseName) ?? blankNormalise(catalogName) ?? '';

    // Blanks are removed BEFORE joining, which is what makes "Vow ()" unreachable rather than
    // merely discouraged.
    const parts = [blankNormalise(severity), blankNormalise(trapping)]
        .filter((part): part is string => part !== null);

    if (parts.length === 0) return base;

    // Trimmed because a missing base must yield "(Forest)", not " (Forest)".
    return `${base} (${parts.join(SEPARATOR)})`.trim();
}
